import React, { useCallback, useState } from "react";
import { Plus } from "lucide-react";
import { createCampaignOffer, listCampaignOffers, updateCampaignOffer } from "../../api/billing";
import { formatDate } from "../../shared/utils/format";
import { DataTable, LoadState, type Column } from "../UserDetails/ui";
import type { CampaignOffer } from "./types";
import {
  Chip,
  ErrorText,
  Field,
  Modal,
  SaveButton,
  Toggle,
  inputClass,
  linkButtonClass,
  primaryButtonClass,
  secondaryButtonClass,
  useAction,
  useLoad,
} from "./ui";

const toDateInput = (iso: string | undefined) => (iso ? iso.slice(0, 10) : "");

const isLive = (c: CampaignOffer) => c.isActive && new Date(c.enrollUntil) >= new Date();

const CampaignFormModal: React.FC<
  Readonly<{ campaign: CampaignOffer | null; onClose: () => void; onSaved: () => void }>
> = ({ campaign, onClose, onSaved }) => {
  const [name, setName] = useState(campaign?.name ?? "");
  const [description, setDescription] = useState(campaign?.description ?? "");
  const [freeDays, setFreeDays] = useState(String(campaign?.freeDays ?? 14));
  const [enrollUntil, setEnrollUntil] = useState(toDateInput(campaign?.enrollUntil));
  const [isActive, setIsActive] = useState(campaign?.isActive ?? true);
  const { busy, error, setError, run } = useAction();

  const save = async () => {
    const days = Number.parseInt(freeDays, 10);
    if (!name.trim()) return setError("Name is required.");
    if (!Number.isInteger(days) || days < 1 || days > 365) return setError("Free days must be 1–365.");
    if (!enrollUntil) return setError("Pick the last signup date.");
    const payload = { name: name.trim(), description: description.trim() || undefined, freeDays: days, enrollUntil, isActive };
    const saved = await run(() =>
      campaign
        ? updateCampaignOffer(campaign.id, { ...payload, description: payload.description ?? null })
        : createCampaignOffer(payload)
    );
    if (saved) onSaved();
  };

  return (
    <Modal
      title={campaign ? `Edit ${campaign.name}` : "New free-days campaign"}
      onClose={onClose}
      busy={busy}
      width="md"
      footer={
        <>
          <button type="button" className={secondaryButtonClass} disabled={busy} onClick={onClose}>
            Cancel
          </button>
          <SaveButton busy={busy} onClick={save}>
            {campaign ? "Save" : "Create"}
          </SaveButton>
        </>
      }
    >
      <div className="space-y-3">
        <Field label="Name">
          <input className={inputClass} value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Description">
          <input className={inputClass} value={description} maxLength={500} onChange={(e) => setDescription(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Free days">
            <input type="number" min={1} max={365} className={inputClass} value={freeDays} onChange={(e) => setFreeDays(e.target.value)} />
          </Field>
          <Field label="Signups until (inclusive)">
            <input type="date" className={inputClass} value={enrollUntil} onChange={(e) => setEnrollUntil(e.target.value)} />
          </Field>
        </div>
        <Toggle checked={isActive} onChange={setIsActive} label="Campaign active" text={isActive ? "Active" : "Off"} />
        <p className="text-xs text-gray-400">Only one campaign runs at a time — switching this on switches the others off.</p>
        <ErrorText message={error} />
      </div>
    </Modal>
  );
};

/** Free-days signup campaigns (`campaign_offers`). */
export const CampaignsTab: React.FC = () => {
  const load = useCallback(() => listCampaignOffers(), []);
  const { data, loading, error, reload } = useLoad(load);
  const [editing, setEditing] = useState<CampaignOffer | "new" | null>(null);
  const toggle = useAction();

  const setActive = async (campaign: CampaignOffer, isActive: boolean) => {
    const done = await toggle.run(() => updateCampaignOffer(campaign.id, { isActive }));
    if (done) await reload();
  };

  const columns: Column<CampaignOffer>[] = [
    {
      header: "Campaign",
      render: (c) => (
        <div>
          <p className="font-medium text-gray-900">{c.name}</p>
          {c.description && <p className="text-xs text-gray-400">{c.description}</p>}
        </div>
      ),
    },
    { header: "Free days", render: (c) => c.freeDays },
    { header: "Signups until", render: (c) => formatDate(c.enrollUntil), className: "whitespace-nowrap" },
    { header: "Status", render: (c) => (isLive(c) ? <Chip tone="emerald">live</Chip> : <Chip>{c.isActive ? "ended" : "off"}</Chip>) },
    {
      header: "Active",
      render: (c) => <Toggle checked={c.isActive} disabled={toggle.busy} label={`${c.name} active`} onChange={(v) => void setActive(c, v)} />,
    },
    {
      header: "",
      render: (c) => (
        <button type="button" className={linkButtonClass} onClick={() => setEditing(c)}>
          Edit
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-white p-3 shadow-sm sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-gray-500">New signups during a live campaign get free access for its number of days.</p>
        <button type="button" className={primaryButtonClass} onClick={() => setEditing("new")}>
          <Plus className="w-4 h-4" /> New campaign
        </button>
      </div>
      {toggle.error && <ErrorText message={toggle.error} />}
      <div className="rounded-xl border border-gray-200 bg-white p-2 shadow-sm">
        {(loading && !data) || error ? (
          <LoadState loading={loading && !data} error={error} onRetry={reload} />
        ) : (
          <DataTable columns={columns} rows={data ?? []} emptyText="No campaigns yet." />
        )}
      </div>
      {editing && (
        <CampaignFormModal
          campaign={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload();
          }}
        />
      )}
    </div>
  );
};
